/**
 * Framework-agnostic stores behind the `./use-magnus-sources` hooks
 * (plan 017 step 5). Each store wraps one slice of the versioned-REST
 * client surface (plans 015/016) in a subscribe/getSnapshot shape, so the
 * React hooks stay thin `useSyncExternalStore` wrappers and the
 * fetch/persistence logic is unit-testable without a React renderer
 * (fetch stubbed at the transport boundary, storage injected).
 */

import type { SdkApprovalResponse } from "@magnus/contract/approvals";
import type { SdkChatSummary } from "@magnus/contract/chats";
import type { SdkFeedbackMap } from "@magnus/contract/feedback";
import type { MagnusUIMessage } from "@magnus/contract/messages";
import type { SdkModel } from "@magnus/contract/models";
import {
  isSdkUploadProcessingTerminal,
  maxFilesPerChat,
  type SdkUploadFinalized,
  uploadAcceptTypes,
} from "@magnus/contract/uploads";
import type { AppSurface, ReasoningLevel } from "@magnus/contract/values";
import type { MagnusClient } from "../client";

/** Reserved selection id meaning "let the server pick the model" — the
 *  server's default, or per-turn Jev routing where that is on. */
export const AUTO_MODEL_SELECTION = "auto";

/** The retired Smart Auto selection, which Auto absorbed. A persisted one is
 *  read back as Auto. */
const LEGACY_SMART_AUTO_SELECTION = "smart-auto";

/** Processing-watch cadence — matches the private composer's poll. */
const PROCESSING_POLL_MS = 3000;
/** Consecutive processing-poll failures tolerated before the watch gives up
 *  and reports a terminal failure (see `watchProcessing`). */
const PROCESSING_POLL_MAX_FAILURES = 5;

// Same keys the private web-app hook uses (`use-model-preference.ts`) so
// the web app and SDK embeds share the preference on the same origin.
const PERSIST_MODEL_KEY = "magnus:persist-model";
const MODEL_PREFERENCE_KEY = "magnus:model-preference";

/** Minimal storage seam — satisfied by `window.localStorage`. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  removeItem(key: string): void;
  setItem(key: string, value: string): void;
}

function defaultStorage(): KeyValueStorage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    // Access throws in some privacy modes / sandboxed iframes.
    return undefined;
  }
}

function toError(err: unknown): Error {
  return err instanceof Error ? err : new Error(String(err));
}

function createEmitter() {
  const listeners = new Set<() => void>();
  return {
    emit() {
      for (const listener of [...listeners]) {
        listener();
      }
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

/** Structurally the block's `ComposerModelsSource` (declared here so the
 *  published SDK doesn't import from the UI package). */
export interface MagnusModelsSource {
  autoRouted?: boolean;
  defaultId?: string;
  defaultReasoningLevel?: string;
  items: SdkModel[];
  loading?: boolean;
  onReasoningLevelChange?: (level: string) => void;
  onSelect: (id: string) => void;
  reasoningLevel?: string;
  selectedId?: string;
  supportsReasoningLevel?: boolean;
}

export interface ModelsStoreState {
  autoRouted?: boolean;
  defaultId?: string;
  defaultReasoningLevel?: ReasoningLevel;
  error?: Error;
  items: SdkModel[];
  loading: boolean;
  /** `AUTO_MODEL_SELECTION` or a model id. */
  selection: string;
}

export interface ModelsStore {
  getState(): ModelsStoreState;
  refresh(): void;
  select(id: string): void;
  subscribe(listener: () => void): () => void;
}

/** Mirrors the private hook's read: persisted choice only counts when the
 *  "remember my model" setting is on. Validation against the live catalog
 *  happens after fetch (`coerceSelection`), not at read time. */
function readStoredSelection(storage: KeyValueStorage | undefined): string {
  if (storage?.getItem(PERSIST_MODEL_KEY) !== "true") {
    return AUTO_MODEL_SELECTION;
  }
  const stored = storage.getItem(MODEL_PREFERENCE_KEY);
  return stored &&
    stored !== AUTO_MODEL_SELECTION &&
    stored !== LEGACY_SMART_AUTO_SELECTION
    ? stored
    : AUTO_MODEL_SELECTION;
}

function coerceSelection(selection: string, items: SdkModel[]): string {
  if (
    selection === AUTO_MODEL_SELECTION ||
    selection === LEGACY_SMART_AUTO_SELECTION
  ) {
    return AUTO_MODEL_SELECTION;
  }
  return items.some((m) => m.id === selection)
    ? selection
    : AUTO_MODEL_SELECTION;
}

export function createModelsStore(options: {
  client: MagnusClient;
  storage?: KeyValueStorage;
}): ModelsStore {
  const { client } = options;
  const storage = options.storage ?? defaultStorage();
  const emitter = createEmitter();
  let state: ModelsStoreState = {
    items: [],
    loading: true,
    selection: readStoredSelection(storage),
  };
  let started = false;
  let generation = 0;

  const set = (patch: Partial<ModelsStoreState>) => {
    state = { ...state, ...patch };
    emitter.emit();
  };

  const load = () => {
    const gen = ++generation;
    set({ error: undefined, loading: true });
    client.models.list().then(
      (list) => {
        if (gen !== generation) {
          return;
        }
        set({
          autoRouted: list.autoRouted,
          defaultId: list.defaultId,
          defaultReasoningLevel: list.defaultReasoningLevel,
          items: list.items,
          loading: false,
          selection: coerceSelection(state.selection, list.items),
        });
      },
      (err: unknown) => {
        if (gen !== generation) {
          return;
        }
        set({ error: toError(err), loading: false });
      }
    );
  };

  return {
    getState: () => state,
    refresh: load,
    select: (id) => {
      set({ selection: id });
      if (storage?.getItem(PERSIST_MODEL_KEY) === "true") {
        storage.setItem(MODEL_PREFERENCE_KEY, id);
      }
    },
    subscribe: (listener) => {
      const unsubscribe = emitter.subscribe(listener);
      if (!started) {
        started = true;
        load();
      }
      return unsubscribe;
    },
  };
}

// ---------------------------------------------------------------------------
// Picker lists (skills / artifacts)
// ---------------------------------------------------------------------------

export interface ListStoreState<T> {
  error?: Error;
  items: T[];
  loading: boolean;
}

export interface ListStore<T> {
  getState(): ListStoreState<T>;
  refresh(): void;
  subscribe(listener: () => void): () => void;
}

export function createListStore<T>(fetcher: () => Promise<T[]>): ListStore<T> {
  const emitter = createEmitter();
  let state: ListStoreState<T> = { items: [], loading: true };
  let started = false;
  let generation = 0;

  const set = (patch: Partial<ListStoreState<T>>) => {
    state = { ...state, ...patch };
    emitter.emit();
  };

  const load = () => {
    const gen = ++generation;
    set({ error: undefined, loading: true });
    fetcher().then(
      (items) => {
        if (gen !== generation) {
          return;
        }
        set({ items, loading: false });
      },
      (err: unknown) => {
        if (gen !== generation) {
          return;
        }
        set({ error: toError(err), loading: false });
      }
    );
  };

  return {
    getState: () => state,
    refresh: load,
    subscribe: (listener) => {
      const unsubscribe = emitter.subscribe(listener);
      if (!started) {
        started = true;
        load();
      }
      return unsubscribe;
    },
  };
}

// ---------------------------------------------------------------------------
// Approvals
// ---------------------------------------------------------------------------

/** Structurally the block's `ChatApprovalsSource` (minus host copy). */
export interface MagnusApprovalsSource {
  submit: (input: SdkApprovalResponse) => Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

/** Structurally the block's `FeedbackSubmitInput`. */
export interface MagnusFeedbackSubmit {
  comment?: string;
  includeFullConversation: boolean;
  messageId: string;
  vote: "up" | "down";
}

/** Structurally the block's `ChatFeedbackSource`. */
export interface MagnusFeedbackSource {
  map: SdkFeedbackMap;
  pending?: boolean;
  submit: (input: MagnusFeedbackSubmit) => Promise<void>;
}

export interface FeedbackStoreState {
  error?: Error;
  loading: boolean;
  map: SdkFeedbackMap;
  pending: boolean;
}

export interface FeedbackStore {
  getState(): FeedbackStoreState;
  refresh(): void;
  /** Rejects on transport failure — the block's submit hook owns retry /
   *  toast / pending UI, so this stays a bare transport promise. */
  submit(input: MagnusFeedbackSubmit): Promise<void>;
  subscribe(listener: () => void): () => void;
}

export function createFeedbackStore(options: {
  chatId: string;
  client: MagnusClient;
  surface: AppSurface;
}): FeedbackStore {
  const { chatId, client, surface } = options;
  const emitter = createEmitter();
  let state: FeedbackStoreState = { loading: true, map: {}, pending: false };
  let started = false;
  let generation = 0;

  const set = (patch: Partial<FeedbackStoreState>) => {
    state = { ...state, ...patch };
    emitter.emit();
  };

  const load = () => {
    const gen = ++generation;
    set({ error: undefined, loading: true });
    client.feedback.get(chatId).then(
      (map) => {
        if (gen !== generation) {
          return;
        }
        set({ loading: false, map });
      },
      (err: unknown) => {
        if (gen !== generation) {
          return;
        }
        set({ error: toError(err), loading: false });
      }
    );
  };

  return {
    getState: () => state,
    refresh: load,
    submit: async (input) => {
      set({ pending: true });
      try {
        await client.feedback.submit(chatId, {
          appSurface: surface,
          comment: input.comment,
          includeFullConversation: input.includeFullConversation,
          messageId: input.messageId,
          vote: input.vote,
        });
      } catch (err) {
        set({ pending: false });
        throw err;
      }
      // Local cache update on submit — the map is keyed by message id, and
      // one row per message means a re-vote just overwrites the entry.
      set({
        map: {
          ...state.map,
          [input.messageId]: {
            submittedAt: new Date().toISOString(),
            vote: input.vote,
          },
        },
        pending: false,
      });
    },
    subscribe: (listener) => {
      const unsubscribe = emitter.subscribe(listener);
      if (!started) {
        started = true;
        load();
      }
      return unsubscribe;
    },
  };
}

// ---------------------------------------------------------------------------
// Chat history
// ---------------------------------------------------------------------------

/** Structurally the block's `ChatHistorySource`. */
export interface MagnusChatHistorySource {
  items: SdkChatSummary[];
  loading?: boolean;
  /**
   * Delete a chat (plan 028). Present → the block's switcher shows an
   * inline confirm-delete action. Rejects on transport failure after the
   * store refetches (the optimistic removal is rolled back).
   */
  onDelete?: (chatId: string) => Promise<void>;
  onNewChat: () => void;
  /**
   * Rename a chat (plan 028). Present → the block's switcher shows an
   * inline rename action. Rejects on transport failure; the store
   * records `error` too.
   */
  onRename?: (chatId: string, title: string) => Promise<void>;
  /**
   * Select a chat. Resolves once its messages finish hydrating (never
   * rejects — failures surface via the hook's `error`); await it when a
   * mount-time restore must settle before creating a fresh chat.
   */
  onSelect: (chatId: string) => Promise<void>;
  /**
   * Refetch the recent-chats list. Switchers call this on open (and from
   * their manual refresh affordance) so a failed/stale initial load
   * recovers without a page reload. Returns a promise that settles when
   * the fetch does (never rejects), so callers can drive local busy
   * affordances off the explicit refresh instead of the store-wide
   * `loading` flag.
   */
  refresh?: () => Promise<void> | void;
  /**
   * Subscribe to list changes (`items` / `loading`); returns unsubscribe.
   * Switchers that stay mounted open (dropdowns) should subscribe locally
   * rather than rely on an ancestor's re-render — under React 19.2
   * StrictMode with a Suspense boundary in the subtree, the reconciler can
   * bind an ancestor's store subscription to a discarded fiber, silencing
   * its updates (facebook/react#35550).
   */
  subscribe?(listener: () => void): () => void;
}

export interface ChatHistoryStoreState {
  /** The selected chat with its hydrated messages; `null` = fresh chat. */
  active: {
    chatId: string;
    initialMessages: MagnusUIMessage[];
    /** In-flight run id — wire to `useMagnus`'s `activeStreamId` so a
     *  reopened chat resumes its live stream. */
    activeStreamId?: string | null;
  } | null;
  error?: Error;
  items: SdkChatSummary[];
  /** True while a selected chat's messages are being fetched. */
  loadingActive: boolean;
  loadingList: boolean;
  /** Remount key for the chat component: the selected chat id, or a fresh
   *  `new-<n>` key that bumps every time "New chat" is chosen. */
  sessionKey: string;
}

export interface ChatHistoryStore {
  /**
   * Drop the store: cancels the pending settle re-poll and invalidates
   * in-flight fetches. The hook calls this when the store is replaced or
   * unmounted — without it a `notifyTurnSettled` fires a list fetch up to
   * {@link TITLE_SETTLE_MS} after the last turn, against a store nobody
   * reads.
   *
   * The store is dropped, not finished: a later `subscribe()` restarts
   * everything the first one kicked off (see the implementation).
   */
  dispose(): void;
  getState(): ChatHistoryStoreState;
  newChat(): void;
  /**
   * A turn just settled (finished streaming). Refetches the list now — a
   * brand-new chat enters recents — and once more after a short settle
   * delay, because the server auto-titles asynchronously after the first
   * turn and the immediate refetch usually still sees the placeholder.
   */
  notifyTurnSettled(): void;
  /** Refetch the list; resolves once the fetch settles (never rejects). */
  refresh(): Promise<void>;
  /**
   * Delete a chat (plan 028). Optimistic: the row leaves the list
   * immediately; a failed delete refetches (restoring the row) and
   * rejects. Deleting the active chat resets to a fresh session once
   * the server confirms.
   */
  remove(chatId: string): Promise<void>;
  /**
   * Rename a chat (plan 028). Follows the feedback-store idiom: awaits
   * the server, then updates the row locally from the response — no
   * optimistic write, since a rename's round trip is fast and a wrong
   * title is worse than a slow one. Rejects on failure after recording
   * `error`.
   */
  rename(chatId: string, title: string): Promise<void>;
  /**
   * Select a chat and hydrate its messages. The returned promise resolves
   * once the fetch settles — successfully or not (it never rejects; failures
   * land in `error`). Hosts that restore a chat id on mount can await this
   * before deciding to create a fresh chat, so the two never race.
   */
  select(chatId: string): Promise<void>;
  subscribe(listener: () => void): () => void;
}

/** Delay before the post-turn re-poll that picks up server auto-titles. */
const TITLE_SETTLE_MS = 4000;

/**
 * A list fetch that never settles (torn dev-server connection, a tab
 * suspended mid-request) must not pin `loadingList` forever — after this
 * window the flag clears with an error so consumers stop showing a busy
 * state. The late-settling fetch still applies its result when it lands.
 */
const LOAD_WATCHDOG_MS = 30_000;

export function createChatHistoryStore(options: {
  client: MagnusClient;
  /** Page size for the recent-chats list. Defaults to 20. */
  limit?: number;
}): ChatHistoryStore {
  const { client, limit = 20 } = options;
  const emitter = createEmitter();
  let nonce = 0;
  let state: ChatHistoryStoreState = {
    active: null,
    items: [],
    loadingActive: false,
    loadingList: true,
    sessionKey: "new-0",
  };
  let started = false;
  // List and active-chat fetches invalidate independently: a select racing an
  // in-flight list load must not discard the list (and vice versa).
  let listGeneration = 0;
  let activeGeneration = 0;
  // One pending settle re-poll at a time; a rapid second turn re-arms it.
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  // Cancels for the watchdogs of every list load still in flight. There can be
  // several — a settle re-poll overlaps an explicit refresh — and they can't
  // collapse into one variable: each `load()` hands its own watchdog to the
  // promise it returns, so cancelling an older one on behalf of a newer would
  // hang whoever is awaiting that older refresh.
  const listWatchdogs = new Set<() => void>();
  // The chat whose hydration owns the current `activeGeneration` while its
  // fetch is in flight; cleared once that fetch settles or is superseded.
  let hydratingChatId: string | undefined;
  // A hydration `dispose()` invalidated before it landed, re-issued by the
  // next `subscribe()` (see `dispose`).
  let resumeChatId: string | undefined;

  const set = (patch: Partial<ChatHistoryStoreState>) => {
    state = { ...state, ...patch };
    emitter.emit();
  };

  const load = (): Promise<void> => {
    const gen = ++listGeneration;
    set({ error: undefined, loadingList: true });
    let settleWatchdog!: () => void;
    const watchdog = new Promise<void>((resolve) => {
      settleWatchdog = resolve;
    });
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Disarms this load's watchdog and settles the promise it backs, so a
    // cancelled watchdog releases its awaiter instead of stranding it.
    const cancelWatchdog = () => {
      if (timer !== undefined) {
        clearTimeout(timer);
        timer = undefined;
      }
      listWatchdogs.delete(cancelWatchdog);
      settleWatchdog();
    };
    timer = setTimeout(() => {
      timer = undefined;
      listWatchdogs.delete(cancelWatchdog);
      if (gen === listGeneration && state.loadingList) {
        set({
          error: new Error("Chat list request did not settle"),
          loadingList: false,
        });
      }
      settleWatchdog();
    }, LOAD_WATCHDOG_MS);
    listWatchdogs.add(cancelWatchdog);
    const done = client.chats.list({ limit }).then(
      (page) => {
        cancelWatchdog();
        if (gen !== listGeneration) {
          return;
        }
        // Applies even after the watchdog fired — a late-settling fetch is
        // a recovery, not a conflict.
        set({ error: undefined, items: page.items, loadingList: false });
      },
      (err: unknown) => {
        cancelWatchdog();
        if (gen !== listGeneration) {
          return;
        }
        set({ error: toError(err), loadingList: false });
      }
    );
    // The returned promise settles on the watchdog too, so busy affordances
    // tracking an explicit refresh always clear.
    return Promise.race([done, watchdog]);
  };

  // Invalidate any in-flight hydration and start a fresh session.
  const resetToFreshChat = () => {
    activeGeneration += 1;
    hydratingChatId = undefined;
    nonce += 1;
    set({
      active: null,
      loadingActive: false,
      sessionKey: `new-${nonce}`,
    });
  };

  const selectChat = (chatId: string): Promise<void> => {
    if (state.active?.chatId === chatId && !state.loadingActive) {
      return Promise.resolve();
    }
    const gen = ++activeGeneration;
    hydratingChatId = chatId;
    set({ error: undefined, loadingActive: true });
    // Both branches are consumed here, so the returned promise always
    // resolves — callers can await settlement without handling rejections.
    return client.chats.get(chatId).then(
      (chat) => {
        if (gen !== activeGeneration) {
          return;
        }
        hydratingChatId = undefined;
        set({
          active: {
            chatId,
            initialMessages: chat.messages,
            activeStreamId: chat.activeStreamId ?? null,
          },
          loadingActive: false,
          sessionKey: chatId,
        });
      },
      (err: unknown) => {
        if (gen !== activeGeneration) {
          return;
        }
        hydratingChatId = undefined;
        set({ error: toError(err), loadingActive: false });
      }
    );
  };

  return {
    dispose: () => {
      if (settleTimer !== undefined) {
        clearTimeout(settleTimer);
        settleTimer = undefined;
      }
      // Nothing stays scheduled past dispose: an armed watchdog would sit in
      // the event loop for up to LOAD_WATCHDOG_MS after unmount. Copied
      // because each cancel removes itself from the set.
      for (const cancelWatchdog of [...listWatchdogs]) {
        cancelWatchdog();
      }
      listGeneration += 1;
      activeGeneration += 1;
      // Every other generation bump hands the loading flags to a replacement
      // fetch that will clear them; this one abandons the work outright, so
      // the store also goes back to un-started — dropped, not finished. React's
      // dev double-mount disposes the memoized store between the two mounts,
      // and without the reset the second mount inherits the first's abandoned
      // fetches: their results discarded by the bumps above, `loadingList` and
      // `loadingActive` pinned true forever, and a host restoring a persisted
      // chat id stuck on its hydration skeleton.
      //
      // Invariant this buys: a `loading*` flag left true past dispose always
      // describes work the next `subscribe()` restarts, never work in flight.
      started = false;
      // Re-issued rather than resumed — the abandoned response is invalid, and
      // only a later subscribe proves anyone still wants the chat.
      resumeChatId = hydratingChatId;
      hydratingChatId = undefined;
    },
    getState: () => state,
    newChat: () => {
      resetToFreshChat();
      // Pick up chats created elsewhere (or by the previous fresh session).
      load();
    },
    notifyTurnSettled: () => {
      load();
      if (settleTimer !== undefined) {
        clearTimeout(settleTimer);
      }
      settleTimer = setTimeout(() => {
        settleTimer = undefined;
        load();
      }, TITLE_SETTLE_MS);
    },
    refresh: load,
    rename: async (chatId, title) => {
      try {
        const updated = await client.chats.rename(chatId, { title });
        set({
          items: state.items.map((item) =>
            item.id === chatId ? { ...item, ...updated } : item
          ),
        });
      } catch (err) {
        set({ error: toError(err) });
        throw err;
      }
    },
    remove: async (chatId) => {
      // Optimistic removal — a failed delete refetches, restoring the row.
      // Bumping the list generation drops any list fetch already in flight
      // (notifyTurnSettled's re-poll is up to TITLE_SETTLE_MS behind), which
      // would otherwise land afterwards and resurrect the deleted row. The
      // optimistic list is authoritative until the delete settles, so it also
      // clears `loadingList` — the abandoned response can no longer do it.
      listGeneration += 1;
      set({
        items: state.items.filter((item) => item.id !== chatId),
        loadingList: false,
      });
      try {
        await client.chats.delete(chatId);
      } catch (err) {
        // Refetch-on-error restores the server truth (load clears `error`
        // synchronously), then the delete failure is what surfaces.
        load();
        set({ error: toError(err) });
        throw err;
      }
      if (state.active?.chatId === chatId) {
        // The open transcript's chat is gone — reset to a fresh session.
        resetToFreshChat();
      }
    },
    select: selectChat,
    subscribe: (listener) => {
      const unsubscribe = emitter.subscribe(listener);
      if (!started) {
        started = true;
        load();
        if (resumeChatId !== undefined) {
          const chatId = resumeChatId;
          resumeChatId = undefined;
          selectChat(chatId);
        }
      }
      return unsubscribe;
    },
  };
}

/**
 * Adapt the store's rejecting `rename`/`remove` into the fire-and-forget
 * callbacks the block's switcher invokes: a failed mutation is logged
 * (plan-023 precedent: fire-and-forget with `console.error`) instead of
 * surfacing as an unhandled promise rejection. `remove`'s refetch-on-error
 * still runs inside the store first, so the list restores the server truth
 * before the rejection is swallowed here. The returned promises resolve
 * (never reject), so hosts may still await settlement.
 */
export function bindHistoryActions(store: ChatHistoryStore): {
  onRename: (chatId: string, title: string) => Promise<void>;
  onDelete: (chatId: string) => Promise<void>;
} {
  return {
    onRename: (chatId, title) =>
      store.rename(chatId, title).catch((err: unknown) => {
        console.error(`Failed to rename chat ${chatId}:`, err);
      }),
    onDelete: (chatId) =>
      store.remove(chatId).catch((err: unknown) => {
        console.error(`Failed to delete chat ${chatId}:`, err);
      }),
  };
}

// ---------------------------------------------------------------------------
// Uploads (plan 023). Unlike the other sources there is no reactive state to
// subscribe to — the composer's staging state machine lives in the block
// (`useAttachments`). The store is the framework-agnostic owner of the
// upload lifecycle: mint → PUT bytes to blob storage → finalize, with
// staged-row cleanup on failure. The byte-PUT is injectable so tests stub it
// (and fetch stays the only transport the mint/finalize/delete legs use).
// ---------------------------------------------------------------------------

/** Progress callback for the byte-PUT leg (0–100). */
export type UploadProgressHandler = (percent: number) => void;

/** Byte-PUT seam — the default implementation uses XHR for upload progress. */
export type UploadPutFile = (
  uploadUrl: string,
  file: File,
  options: { onProgress?: UploadProgressHandler; signal?: AbortSignal }
) => Promise<void>;

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("The operation was aborted.", "AbortError");
  }
}

/**
 * PUT bytes to blob storage with real upload progress (XHR — fetch request
 * streams are not portable across browsers). The UI tolerates 0→100 jumps
 * when `onProgress` never fires (e.g. non-browser runtimes without XHR).
 */
export const putFileToBlob: UploadPutFile = (uploadUrl, file, options) =>
  new Promise<void>((resolve, reject) => {
    if (typeof XMLHttpRequest === "undefined") {
      reject(
        new Error("Blob upload requires XMLHttpRequest (browser runtime)")
      );
      return;
    }
    const xhr = new XMLHttpRequest();
    const { onProgress, signal } = options;
    const onSignalAbort = () => xhr.abort();
    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable) {
        onProgress?.(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener("load", () => {
      signal?.removeEventListener("abort", onSignalAbort);
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(
          new Error(`Blob upload failed: ${xhr.status} ${xhr.statusText}`)
        );
      }
    });
    xhr.addEventListener("error", () => {
      signal?.removeEventListener("abort", onSignalAbort);
      reject(new Error("Blob upload failed: network error"));
    });
    xhr.addEventListener("abort", () => {
      signal?.removeEventListener("abort", onSignalAbort);
      reject(new DOMException("The operation was aborted.", "AbortError"));
    });
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException("The operation was aborted.", "AbortError"));
        return;
      }
      signal.addEventListener("abort", onSignalAbort, { once: true });
    }
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
    xhr.setRequestHeader(
      "Content-Type",
      file.type || "application/octet-stream"
    );
    xhr.send(file);
  });

/**
 * The block's `ComposerUploadsSource` shape, owned by the SDK. `accept` +
 * `maxFiles` are the contract-pinned staging limits; `upload` runs the whole
 * direct-upload lifecycle against one chat.
 */
export interface MagnusUploadsSource {
  /** `<input accept>` values (MIME types + bare extensions). */
  readonly accept: readonly string[];
  /**
   * Delete an upload's row and blobs. The block calls this fire-and-forget
   * when a finalized tile is removed before send — the stale-staging reaper
   * only sweeps unfinalized rows, so a finalized row must be deleted here.
   */
  delete(uploadId: string): Promise<void>;
  /** Max staged attachments per message. */
  readonly maxFiles: number;
  /**
   * The current URL for an upload (`uploads.get`). Persisted document parts
   * carry only the upload id, so the block's attachment preview looks the
   * URL up on click (plan 043 P9). Optional: without it, sent document
   * tiles stay static (sent images and staged files still preview).
   */
  resolveUrl?(uploadId: string): Promise<string>;
  /**
   * Re-run processing for a sent upload whose pipeline failed (or whose
   * rich pass failed). The block calls it from a failed sent tile's retry
   * and then resumes `watchProcessing` (plan 043 P8). Optional: without it,
   * a failed sent tile shows the failed dot with no retry.
   */
  retryProcessing?(uploadId: string): Promise<void>;
  /**
   * Mint → PUT → finalize. Resolves the finalized row (url/storageKey/
   * processingStatus); rejects on failure and deletes the staged row so a
   * retry starts clean. `signal` aborts the in-flight PUT and finalize
   * (tile remove / composer clear); mint is short and the signal is
   * checked before each subsequent leg.
   */
  upload(
    file: File,
    onProgress?: UploadProgressHandler,
    signal?: AbortSignal
  ): Promise<SdkUploadFinalized>;
  /**
   * Processing watch (ADR-055 text-ready gate, plan 024 C10): polls
   * `uploads.get` until the pipeline reports terminal, forwarding each
   * snapshot with a source-computed `terminal` flag. Returns an
   * unsubscribe (the block calls it on tile remove / composer clear /
   * unmount).
   *
   * An upload the server no longer has (404 / `NOT_FOUND`) stops the watch
   * at once with a terminal `failed` report flagged `gone: true`, so the
   * composer's gate lifts and the sent-attachment statuses can show the
   * tile as settled rather than offering a retry that would 404 too.
   */
  watchProcessing(
    uploadId: string,
    onUpdate: (update: {
      /** The server no longer has this upload (deleted). Terminal. */
      gone?: boolean;
      status: SdkUploadFinalized["processingStatus"];
      richPassFailed?: boolean | null;
      terminal: boolean;
    }) => void
  ): () => void;
}

export type UploadsStore = MagnusUploadsSource;

/** A "this upload doesn't exist" failure from `uploads.get`: the REST
 *  client's `status: 404`, or a `NOT_FOUND` code (the REST envelope, or a
 *  tRPC error's `data`) from a host-supplied client. */
function isNotFoundError(err: unknown): boolean {
  if (typeof err !== "object" || err === null) {
    return false;
  }
  const { code, data, status } = err as {
    code?: unknown;
    data?: { code?: unknown; httpStatus?: unknown } | null;
    status?: unknown;
  };
  return (
    status === 404 ||
    code === "NOT_FOUND" ||
    data?.code === "NOT_FOUND" ||
    data?.httpStatus === 404
  );
}

export function createUploadsStore(options: {
  chatId: string;
  client: MagnusClient;
  putFile?: UploadPutFile;
}): UploadsStore {
  const { chatId, client } = options;
  const putFile = options.putFile ?? putFileToBlob;
  // Optional on `MagnusClient`: a client without it yields a source without
  // it, and the block then offers no retry on a failed sent tile.
  const retryUpload = client.uploads.retryProcessing?.bind(client.uploads);

  return {
    accept: uploadAcceptTypes,
    maxFiles: maxFilesPerChat,
    delete: (uploadId) => client.uploads.delete(uploadId),
    resolveUrl: async (uploadId) => (await client.uploads.get(uploadId)).url,
    ...(retryUpload
      ? { retryProcessing: (uploadId: string) => retryUpload(uploadId) }
      : {}),
    upload: async (file, onProgress, signal) => {
      // Tracks the staged row until finalize succeeds so failures (and
      // aborts) delete it — the same cleanup the private composer hook does.
      let stagedUploadId: string | undefined;
      try {
        const minted = await client.uploads.create({
          filename: file.name,
          mediaType: file.type || "application/octet-stream",
          size: file.size,
          target: { chatId, kind: "chat" },
        });
        stagedUploadId = minted.uploadId;
        throwIfAborted(signal);
        await putFile(minted.uploadUrl, file, { onProgress, signal });
        throwIfAborted(signal);
        // The signal covers finalize too: aborting mid-finalize rejects here
        // and the catch deletes the row (the server may have finalized it —
        // delete removes it either way).
        const finalized = await client.uploads.finalize(minted.uploadId, {
          signal,
        });
        // Finalize owns the row from here — a later failure must not delete it.
        stagedUploadId = undefined;
        return finalized;
      } catch (err) {
        if (stagedUploadId) {
          // Fire-and-forget: the row may not exist yet (mint failed) or the
          // delete may race the abort — either way the cleanup is
          // best-effort.
          client.uploads.delete(stagedUploadId).catch(() => undefined);
        }
        throw err;
      }
    },
    watchProcessing: (uploadId, onUpdate) => {
      let cancelled = false;
      let failures = 0;
      let timer: ReturnType<typeof setTimeout> | undefined;
      const schedule = (delayMs: number) => {
        timer = setTimeout(() => {
          poll().catch(() => undefined);
        }, delayMs);
      };
      const poll = async () => {
        let snapshot: {
          richPassFailed: boolean | null;
          status: SdkUploadFinalized["processingStatus"];
        };
        try {
          const row = await client.uploads.get(uploadId);
          if (cancelled) {
            return;
          }
          snapshot = {
            status: row.processingStatus ?? null,
            richPassFailed: row.richPassFailed ?? null,
          };
          failures = 0;
        } catch (err) {
          if (cancelled) {
            return;
          }
          // The row is gone (deleted since it was sent, or never ours):
          // retrying can't bring it back, so stop now rather than burning
          // the backoff budget on guaranteed 404s.
          if (isNotFoundError(err)) {
            dispatch({
              gone: true,
              richPassFailed: null,
              status: "failed",
              terminal: true,
            });
            return;
          }
          // A failed poll is "unknown", not terminal (mirrors the workflow-run
          // status contract), so retry — but with a bound and a backoff. A
          // permanent failure (the row was deleted, the session expired) would
          // otherwise retry at the flat cadence forever while the consumer's
          // send gate, which only lifts on a terminal report, stayed held.
          failures += 1;
          if (failures < PROCESSING_POLL_MAX_FAILURES) {
            schedule(PROCESSING_POLL_MS * 2 ** (failures - 1));
            return;
          }
          // Out of retries: report a terminal failure so the gate lifts and
          // the tile shows its failed state (with a retry affordance) rather
          // than a send button that never re-enables.
          dispatch({ richPassFailed: null, status: "failed", terminal: true });
          return;
        }
        const terminal = isSdkUploadProcessingTerminal(snapshot);
        dispatch({ ...snapshot, terminal });
        if (!terminal) {
          schedule(PROCESSING_POLL_MS);
        }
      };
      // Dispatched outside the fetch's try/catch: a consumer callback that
      // throws is a consumer bug, not a failed poll, and must not be retried
      // as one — doing so restarts polling past a terminal report.
      function dispatch(update: Parameters<typeof onUpdate>[0]) {
        try {
          onUpdate(update);
        } catch (err) {
          console.error("[magnus] watchProcessing listener threw", err);
        }
      }
      poll().catch(() => undefined);
      return () => {
        cancelled = true;
        if (timer !== undefined) {
          clearTimeout(timer);
        }
      };
    },
  };
}

// ---------------------------------------------------------------------------
// Dictation (plan 027). Mic → batched Cartesia STT → transcript, over the
// versioned `/api/sdk/v1/voice/ws-token` mint. The token cache ports
// `@workspace/voice-shared`'s `createTokenCache` semantics (freshness
// margin, min-TTL floor, in-flight dedupe, invalidate-on-auth-failure) and
// the transcription leg ports `transcribeBatch` (direct multipart POST to
// Cartesia, one retry on an auth-shaped rejection) — the SDK must not
// depend on the private voice package. Streaming STT over WS is the
// recorded follow-on, deliberately not ported here.
// ---------------------------------------------------------------------------

/** Batch STT endpoint + model (Cartesia `POST /stt` — one utterance per
 *  call). */
export const DICTATION_STT_URL = "https://api.cartesia.ai/stt";
export const DICTATION_STT_MODEL = "ink-whisper";

/** Token-cache tuning, ported from `createTokenCache`'s defaults. */
const DICTATION_TOKEN_FRESHNESS_MARGIN_MS = 60_000;
const DICTATION_TOKEN_MIN_TTL_MS = 60_000;
const DICTATION_TOKEN_FALLBACK_TTL_MS = 600_000;

export type MagnusDictationState =
  | "idle"
  | "requesting-token"
  | "listening"
  | "finalizing"
  | "error";

/**
 * The block's `ComposerDictationSource` shape, owned by the SDK. The
 * store's methods never reject — every failure lands in `error` with
 * `state: "error"` (sticky until the next `start()`).
 */
export interface MagnusDictationSource {
  error?: Error;
  start(): void;
  state: MagnusDictationState;
  stop(): void;
  /** The latest finalized transcript. Cleared on the next `start()`, so
   *  each dictation turn is a fresh edge for the composer's insert
   *  effect — even when two turns transcribe to the same words. */
  transcript: string;
}

export interface DictationStoreState {
  error?: Error;
  state: MagnusDictationState;
  transcript: string;
}

export interface DictationStore {
  /**
   * Drop the store: releases the microphone and invalidates any in-flight
   * turn. The hook calls this when the store is replaced or unmounted —
   * without it, unmounting mid-dictation orphans a live `MediaStream` and
   * the browser keeps showing the recording indicator with nothing left to
   * stop it.
   */
  dispose(): void;
  getState(): DictationStoreState;
  /** Begin a dictation turn (token mint → mic capture). No-op unless
   *  idle/error. Never rejects — failures land in `state.error`. */
  start(): Promise<void>;
  /** Finalize: stop capture, transcribe, set `transcript`. No-op unless
   *  listening. Never rejects. */
  stop(): Promise<void>;
  subscribe(listener: () => void): () => void;
}

/**
 * Audio-capture seam — the one platform-specific piece. `start` acquires
 * the mic (rejects on denial), `stop` resolves the captured audio,
 * `cancel` tears down without producing audio. The default is a
 * MediaRecorder implementation; tests and non-browser runtimes inject
 * their own.
 */
export interface DictationRecorder {
  cancel(): void;
  start(): Promise<void>;
  stop(): Promise<Blob>;
}

/** Browser default: one `MediaRecorder` over a `getUserMedia` stream. */
export function createMediaRecorderDictationRecorder(): DictationRecorder {
  let stream: MediaStream | null = null;
  let recorder: MediaRecorder | null = null;
  let chunks: Blob[] = [];

  const teardown = () => {
    for (const track of stream?.getTracks() ?? []) {
      try {
        track.stop();
      } catch {
        // Track already stopped — nothing to do.
      }
    }
    stream = null;
    recorder = null;
    chunks = [];
  };

  return {
    async start() {
      if (
        typeof navigator === "undefined" ||
        typeof MediaRecorder === "undefined"
      ) {
        throw new Error("Dictation needs a browser with microphone support");
      }
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
      chunks = [];
      recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      recorder.start();
    },
    stop() {
      const active = recorder;
      if (!active) {
        return Promise.reject(new Error("Recorder is not running"));
      }
      return new Promise<Blob>((resolve) => {
        active.onstop = () => {
          const blob = new Blob(chunks, {
            type: active.mimeType || "audio/webm",
          });
          teardown();
          resolve(blob);
        };
        if (active.state === "inactive") {
          // Already stopped — onstop won't fire again.
          const blob = new Blob(chunks, {
            type: active.mimeType || "audio/webm",
          });
          teardown();
          resolve(blob);
        } else {
          active.stop();
        }
      });
    },
    cancel() {
      const active = recorder;
      if (active && active.state !== "inactive") {
        active.onstop = null;
        try {
          active.stop();
        } catch {
          // Ignore — teardown below releases the mic regardless.
        }
      }
      teardown();
    },
  };
}

/** Multipart filename for the STT POST, derived from the capture's
 *  container (Safari records mp4/aac, Chrome/Firefox webm/opus). */
function dictationFilename(mimeType: string): string {
  if (mimeType.includes("mp4")) {
    return "dictation.mp4";
  }
  if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
    return "dictation.mp3";
  }
  if (mimeType.includes("wav")) {
    return "dictation.wav";
  }
  return "dictation.webm";
}

function dictationStartError(err: unknown): Error {
  if (err instanceof Error && err.name === "NotAllowedError") {
    return new Error("Microphone access was denied.");
  }
  return toError(err);
}

export function createDictationStore(options: {
  client: MagnusClient;
  /** Capture seam. Defaults to the MediaRecorder implementation. */
  createRecorder?: () => DictationRecorder;
  /**
   * Fetch for the direct-to-Cartesia STT POST. Deliberately NOT the
   * client's auth-applied fetch — Magnus credentials must never leak to
   * a third-party origin. Defaults to the global.
   */
  sttFetch?: typeof globalThis.fetch;
  /** Optional warning logger (token-fetch failures degrade quietly). */
  warn?: (...args: unknown[]) => void;
}): DictationStore {
  const { client } = options;
  const createRecorder =
    options.createRecorder ?? createMediaRecorderDictationRecorder;
  const sttFetch = options.sttFetch ?? globalThis.fetch.bind(globalThis);
  const warn = options.warn ?? (() => undefined);

  const emitter = createEmitter();
  let state: DictationStoreState = { state: "idle", transcript: "" };
  // Bumped by every start() so a late finalization from a superseded
  // session can't write its transcript over the new one.
  let generation = 0;
  let activeRecorder: DictationRecorder | undefined;

  const set = (patch: Partial<DictationStoreState>) => {
    state = { ...state, ...patch };
    emitter.emit();
  };

  // --- Token cache (createTokenCache port) --------------------------------

  interface CachedDictationToken {
    cartesiaVersion: string;
    expiresAtMs: number;
    token: string;
  }

  let cachedToken: CachedDictationToken | null = null;
  let inFlightToken: Promise<CachedDictationToken | null> | null = null;

  const fetchFreshToken = async (): Promise<CachedDictationToken | null> => {
    try {
      const minted = await client.voice.mintToken("stt");
      const parsedMs = Date.parse(minted.expiresAt);
      // Clamp the TTL at the min-TTL floor so a nonsensical upstream
      // expiry can't poison the cache (ported semantics).
      const ttlMs = Number.isNaN(parsedMs)
        ? DICTATION_TOKEN_FALLBACK_TTL_MS
        : Math.max(DICTATION_TOKEN_MIN_TTL_MS, parsedMs - Date.now());
      return {
        token: minted.token,
        cartesiaVersion: minted.cartesiaVersion,
        expiresAtMs: Date.now() + ttlMs,
      };
    } catch (err) {
      warn("[dictation] token mint failed", err);
      return null;
    }
  };

  const getToken = (): Promise<CachedDictationToken | null> => {
    if (
      cachedToken &&
      cachedToken.expiresAtMs > Date.now() + DICTATION_TOKEN_FRESHNESS_MARGIN_MS
    ) {
      return Promise.resolve(cachedToken);
    }
    if (inFlightToken) {
      return inFlightToken;
    }
    const promise = fetchFreshToken().then((token) => {
      inFlightToken = null;
      cachedToken = token ?? null;
      return token;
    });
    inFlightToken = promise;
    return promise;
  };

  const invalidateToken = () => {
    cachedToken = null;
  };

  // --- Batch STT (transcribeBatch port) -----------------------------------

  const transcribe = async (blob: Blob): Promise<string> => {
    const attempt = async (token: CachedDictationToken) => {
      const form = new FormData();
      form.set("file", blob, dictationFilename(blob.type));
      form.set("model", DICTATION_STT_MODEL);
      const res = await sttFetch(DICTATION_STT_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token.token}`,
          "Cartesia-Version": token.cartesiaVersion,
        },
        body: form,
      });
      return { status: res.status, body: await res.text().catch(() => "") };
    };

    let token = await getToken();
    if (!token) {
      throw new Error("Could not get a transcription token.");
    }
    let res = await attempt(token);
    // One transparent retry with a fresh token when Cartesia rejects the
    // cached one (expired / key rotated).
    if (res.status === 401 || res.status === 403) {
      invalidateToken();
      token = await getToken();
      if (!token) {
        throw new Error("Could not refresh the transcription token.");
      }
      res = await attempt(token);
    }
    if (res.status < 200 || res.status >= 300) {
      throw new Error(
        `Transcription failed (${res.status}): ${res.body.slice(0, 120)}`
      );
    }
    try {
      const payload = JSON.parse(res.body) as { text?: unknown };
      return typeof payload.text === "string" ? payload.text : "";
    } catch {
      throw new Error("Transcription failed: unexpected upstream response");
    }
  };

  // --- State machine --------------------------------------------------------

  return {
    dispose: () => {
      // Bumping the generation first means a start()/stop() already awaiting
      // a token or a transcription bails instead of writing to a store
      // nobody reads (and instead of re-acquiring the mic after teardown).
      generation += 1;
      activeRecorder?.cancel();
      activeRecorder = undefined;
    },
    getState: () => state,
    start: async () => {
      const current = state.state;
      if (current !== "idle" && current !== "error") {
        return;
      }
      const gen = ++generation;
      set({ state: "requesting-token", error: undefined, transcript: "" });
      let recorder: DictationRecorder | undefined;
      try {
        const token = await getToken();
        if (!token) {
          throw new Error("Could not get a transcription token.");
        }
        if (gen !== generation) {
          return;
        }
        recorder = createRecorder();
        activeRecorder = recorder;
        await recorder.start();
        if (gen !== generation) {
          recorder.cancel();
          return;
        }
        set({ state: "listening" });
      } catch (err) {
        recorder?.cancel();
        if (activeRecorder === recorder) {
          activeRecorder = undefined;
        }
        if (gen !== generation) {
          return;
        }
        set({ state: "error", error: dictationStartError(err) });
      }
    },
    stop: async () => {
      if (state.state !== "listening") {
        return;
      }
      const gen = generation;
      const recorder = activeRecorder;
      activeRecorder = undefined;
      set({ state: "finalizing" });
      try {
        const blob = recorder ? await recorder.stop() : undefined;
        if (!blob || blob.size === 0) {
          // Nothing captured (instant tap) — back to idle quietly.
          if (gen === generation) {
            set({ state: "idle", transcript: "" });
          }
          return;
        }
        const text = (await transcribe(blob)).trim();
        if (gen !== generation) {
          return;
        }
        set({ state: "idle", transcript: text });
      } catch (err) {
        if (gen !== generation) {
          return;
        }
        set({ state: "error", error: toError(err) });
      }
    },
    subscribe: (listener) => emitter.subscribe(listener),
  };
}
